/**
 * The "Rules and countries" content and its lookups (pure functions, tested).
 *
 * Every card has an id that is unique over the rules for every country and all country
 * specialities, so `#<id>` opens it on its page. A case reason carries a registry rule id
 * (`gate.competitor`); `whyFor` turns it into the card that explains it.
 */

import { GLOSSARY_RULES, HOW_IT_WORKS, WARNING_OR_BLOCKED } from "./generated/glossary";
import { GUIDE_DENMARK } from "./generated/guide-denmark";
import { GUIDE_FRANCE } from "./generated/guide-france";
import { GUIDE_GERMANY } from "./generated/guide-germany";
import { GUIDE_INDIA } from "./generated/guide-india";
import { GUIDE_NORTH_AMERICA } from "./generated/guide-north-america";
import { GUIDE_SPAIN_MEXICO_CZECHIA } from "./generated/guide-spain-mexico-czechia";
import { GUIDE_UNITED_KINGDOM } from "./generated/guide-united-kingdom";
import type {
  CountryGuide,
  Explanation,
  GuideId,
  RegistryMode,
  RuleStatus,
  RuleTopic,
  SeenTone,
} from "./types";
import { WHY_TARGETS } from "./why-targets";

export * from "./types";
export { GLOSSARY_RULES, HOW_IT_WORKS, WARNING_OR_BLOCKED, WHY_TARGETS };

/** The guides in the order of the overview. */
export const COUNTRY_GUIDES: readonly CountryGuide[] = [
  GUIDE_DENMARK,
  GUIDE_UNITED_KINGDOM,
  GUIDE_NORTH_AMERICA,
  GUIDE_INDIA,
  GUIDE_FRANCE,
  GUIDE_GERMANY,
  GUIDE_SPAIN_MEXICO_CZECHIA,
];

/** The words of a topic, for the filter chips. */
export const TOPIC_LABEL: Record<RuleTopic, string> = {
  "mail-and-case": "Mail and case",
  customer: "Customer",
  items: "Items",
  price: "Price and margin",
  freight: "Freight",
  tax: "Tax",
  quotation: "Quotation",
};

export const STATUS_LABEL: Record<RuleStatus, string> = {
  active: "Active",
  planned: "Planned",
};

export const TONE_LABEL: Record<SeenTone, string> = {
  blocked: "Blocked",
  warning: "Warning",
  info: "Info",
};

function norm(code: unknown): string {
  return typeof code === "string" ? code.trim().toLowerCase() : "";
}

/** The guide's name on a chip: "United Kingdom", never "the United Kingdom". */
export function guideLabel(guide: CountryGuide): string {
  return guide.shortName.replace(/^the\s+/i, "");
}

/** Short URL segments of a country that shares a guide ("/rules/czechia"). */
const GUIDE_ALIASES: Record<string, string> = {
  czechia: "spain-mexico-czechia",
  "czech-republic": "spain-mexico-czechia",
  spain: "spain-mexico-czechia",
  mexico: "spain-mexico-czechia",
};

/** The guide of a URL segment, also by a country alias. */
export function guideById(id: string | null | undefined): CountryGuide | undefined {
  const key = norm(id);
  const target = GUIDE_ALIASES[key] ?? key;
  return COUNTRY_GUIDES.find((guide) => guide.id === target);
}

/** The guide of an area: an entity code of a case (`wgg`) or a role code (`de`). */
export function guideForArea(code: string | null | undefined): CountryGuide | undefined {
  const key = norm(code);
  if (key === "") {
    return undefined;
  }
  return (
    COUNTRY_GUIDES.find((guide) => guide.entities.includes(key)) ??
    COUNTRY_GUIDES.find((guide) => guide.areaRoles.includes(key))
  );
}

/** The first guide of a list of area codes (`["dk", "de"]`), skipping "all". */
export function firstGuideOf(areas: readonly string[]): CountryGuide | undefined {
  for (const area of areas) {
    const guide = guideForArea(area);
    if (guide) {
      return guide;
    }
  }
  return undefined;
}

export interface Located {
  rule: Explanation;
  /** The guide of a country speciality; null for a rule for every country. */
  guide: CountryGuide | null;
}

/** Where a card lives: the glossary or a guide. A former id (`renamedFrom`) also works. */
export function findExplanation(id: string | null | undefined): Located | null {
  if (!id) {
    return null;
  }
  const rule = GLOSSARY_RULES.find((entry) => entry.id === id || entry.renamedFrom === id);
  if (rule) {
    return { rule, guide: null };
  }
  for (const guide of COUNTRY_GUIDES) {
    const card = guide.specialities.find((entry) => entry.id === id || entry.renamedFrom === id);
    if (card) {
      return { rule: card, guide };
    }
  }
  return null;
}

/** The page and anchor of a card: `/rules/denmark#dk-agents`, `/rules/glossary#freight`. */
export function explanationHref(located: Located): string {
  return `/rules/${located.guide ? located.guide.id : "glossary"}#${located.rule.id}`;
}

/** The rules for every country a speciality belongs to. */
export function glossaryRulesOf(rule: Explanation): Explanation[] {
  return rule.glossaryIds
    .map((id) => GLOSSARY_RULES.find((entry) => entry.id === id))
    .filter((entry): entry is Explanation => entry !== undefined);
}

/** The mode of a registry rule for an entity, from a card's snapshot (null when unknown). */
export function snapshotMode(
  rule: Explanation,
  ruleId: string,
  entity: string,
): RegistryMode | null {
  const modes = rule.modes?.[ruleId];
  if (!modes) {
    return null;
  }
  return modes[entity] ?? modes.default ?? null;
}

/**
 * True when a rule runs for at least one of the entities (mode not off). A card without
 * a registry rule is about I2Q in general and counts as running everywhere.
 */
export function runsFor(rule: Explanation, entities: readonly string[]): boolean {
  if (!rule.modes || rule.registryIds.length === 0) {
    return true;
  }
  return rule.registryIds.some((ruleId) =>
    entities.some((entity) => {
      const mode = snapshotMode(rule, ruleId, entity);
      return mode === null || mode !== "off";
    }),
  );
}

const MODE_TONE: Record<Exclude<RegistryMode, "off">, SeenTone> = {
  block: "blocked",
  warn: "warning",
  info: "info",
};
const TONE_RANK: Record<SeenTone, number> = { blocked: 0, warning: 1, info: 2 };

/**
 * What sales sees for a rule in these companies. A rule for every country can be a block
 * in one company and a warning in another, so the strongest mode of its registry rules
 * there can lower the card's own tone. Without a mode, the card's own tone.
 */
export function toneIn(
  rule: Explanation,
  entities: readonly string[],
  /** Only these registry rules of the card (a "Why?" pop-up names one rule). */
  only?: readonly string[],
): SeenTone {
  const tones: SeenTone[] = [];
  for (const ruleId of rule.registryIds.filter((id) => !only || only.includes(id))) {
    for (const entity of entities) {
      const mode = snapshotMode(rule, ruleId, entity);
      if (mode && mode !== "off") {
        tones.push(MODE_TONE[mode]);
      }
    }
  }
  const strongest = tones.sort((a, b) => TONE_RANK[a] - TONE_RANK[b])[0];
  if (!strongest) {
    return rule.tone;
  }
  // The weaker of the two: a mode only lowers the card's tone (a block that is a warning
  // in India), never raises it (freight is "warn" but shows a note where it is computed).
  return TONE_RANK[strongest] > TONE_RANK[rule.tone] ? strongest : rule.tone;
}

/** All words a search looks at, lower-cased. */
function textOf(rule: Explanation): string {
  return [
    rule.title,
    rule.checks,
    rule.why,
    rule.youSee,
    rule.whatToDo,
    rule.example,
    rule.plannedPart ?? "",
    TOPIC_LABEL[rule.topic],
    ...rule.registryIds,
  ]
    .join(" ")
    .toLowerCase();
}

/** The words of a search query (at most 60 characters are read). */
export function queryWords(text: string | null | undefined): string[] {
  return (text ?? "")
    .slice(0, 60)
    .toLowerCase()
    .split(/\s+/)
    .map((word) => word.trim())
    .filter(Boolean);
}

export interface RuleFilter {
  text?: string | null;
  status?: RuleStatus | null;
  tone?: SeenTone | null;
  topic?: RuleTopic | null;
}

/** The rules that match every word of the text and every filter that is set. */
export function filterRules(rules: readonly Explanation[], filter: RuleFilter): Explanation[] {
  const words = queryWords(filter.text);
  return rules.filter((rule) => {
    if (
      (filter.status && rule.status !== filter.status) ||
      (filter.tone && rule.tone !== filter.tone) ||
      (filter.topic && rule.topic !== filter.topic)
    ) {
      return false;
    }
    if (words.length === 0) {
      return true;
    }
    const text = textOf(rule);
    return words.every((word) => text.includes(word));
  });
}

/** A to Z by title. */
export function byTitle(rules: readonly Explanation[]): Explanation[] {
  return [...rules].sort((a, b) => a.title.localeCompare(b.title, "en"));
}

export interface SearchResult {
  /** Rules for every country (only those that run in the chosen guide's companies). */
  glossary: Explanation[];
  /** Country specialities, per guide (only the chosen guide when one is set). */
  specialities: { guide: CountryGuide; rules: Explanation[] }[];
  total: number;
}

/** The glossary search: rules for every country and the specialities of the guides. */
export function searchRules(filter: RuleFilter & { guide?: GuideId | null }): SearchResult {
  const guide = guideById(filter.guide ?? null) ?? null;
  const glossary = byTitle(
    filterRules(GLOSSARY_RULES, filter).filter((rule) => !guide || runsFor(rule, guide.entities)),
  );
  const specialities = COUNTRY_GUIDES.filter((entry) => !guide || entry.id === guide.id)
    .map((entry) => ({ guide: entry, rules: filterRules(entry.specialities, filter) }))
    .filter((entry) => entry.rules.length > 0);
  const total = glossary.length + specialities.reduce((sum, entry) => sum + entry.rules.length, 0);
  return { glossary, specialities, total };
}

export interface WhyExplanation {
  /** The card the pop-up shows: the speciality of the case's country when there is one. */
  primary: Located;
  /** The rule for every country, when the pop-up shows a speciality. */
  general: Located | null;
  /** The guide of the case's company. */
  guide: CountryGuide | null;
  /** The company of the case, lower case ("" when unknown). */
  entity: string;
  /** The registry rule id of the reason. */
  ruleId: string;
}

/** The target id of a registry rule for an entity (`WHY_TARGETS`). */
function targetId(ruleId: string, entity: string): string | null {
  const target = Object.hasOwn(WHY_TARGETS, ruleId) ? WHY_TARGETS[ruleId] : undefined;
  if (!target) {
    return null;
  }
  if (typeof target === "string") {
    return target;
  }
  return target[entity] ?? Object.values(target)[0] ?? null;
}

/**
 * Line reason codes (not registry rules) -> the card that explains them. A line read from
 * a photo (`photo_read`) or a scanned page (`scan_read`) always needs a decision.
 */
export const REASON_CARDS: Readonly<Record<string, string>> = {
  photo_read: "line-from-a-picture",
  scan_read: "line-from-a-picture",
};

/** The explanation of a line reason code; null for a code without a card. */
export function whyForReason(code: string | null | undefined): WhyExplanation | null {
  const key = norm(code);
  const id = Object.hasOwn(REASON_CARDS, key) ? REASON_CARDS[key] : undefined;
  const target = findExplanation(id);
  if (!target) {
    return null;
  }
  return { primary: target, general: null, guide: null, entity: "", ruleId: key };
}

/**
 * The explanation of a reason on a case: the registry rule id plus the case's company.
 * Null for an id no explanation is bound to (the "Why?" link is then left out).
 */
export function whyFor(
  ruleId: string | null | undefined,
  entity: string | null | undefined,
): WhyExplanation | null {
  const key = norm(entity);
  const id = targetId(ruleId ?? "", key);
  const target = findExplanation(id);
  if (!ruleId || !target) {
    return null;
  }
  const guide = guideForArea(key) ?? target.guide ?? null;
  // A speciality of the case's country that explains the same rule wins: it says how the
  // rule behaves for this company. Prefer one whose snapshot names the entity.
  const cards = (guide?.specialities ?? []).filter(
    (card) => card.registryIds.includes(ruleId) && card.id !== target.rule.id,
  );
  const named = cards.find((card) => card.modes?.[ruleId]?.[key] !== undefined);
  const card = named ?? cards[0];
  if (card && guide) {
    return { primary: { rule: card, guide }, general: target, guide, entity: key, ruleId };
  }
  return { primary: target, general: null, guide, entity: key, ruleId };
}
